import { NpcType } from '../npcs/types.js';
// ============================================================================
// Cause of Death
// ============================================================================
export const CauseOfDeath = {
    UNKNOWN: 0,
    COMBAT: 1,
    STARVATION: 2,
    ANIMAL_ATTACK: 3,
    EXECUTION: 4,
    EXPOSURE: 5,
    ILLNESS: 6,
};
// ============================================================================
// Rot Stages
// ============================================================================
export const RotStage = {
    FRESH: 0, // 0-0.3 rot - full quality
    STALE: 1, // 0.3-0.6 rot - reduced quality
    ROTTED: 2, // 0.6-0.9 rot - unusable, mood debuff
    SKELETON: 3, // 0.9-1.0 rot - bones only (pawns persist, animals despawn)
};
export const getRotStageFromProgress = ({ rotProgress, }) => {
    if (rotProgress < 0.3) {
        return RotStage.FRESH;
    }
    if (rotProgress < 0.6) {
        return RotStage.STALE;
    }
    if (rotProgress < 0.9) {
        return RotStage.ROTTED;
    }
    return RotStage.SKELETON;
};
// ============================================================================
// Corpse Despawn Reasons
// ============================================================================
export const CorpseDespawnReason = {
    BUTCHERED: 0,
    DECAYED: 1,
    BURNED: 2,
    BURIED: 3,
    DESTROYED: 4,
};
// Base yields - actual yield scales with rot progress and butcher skill
export const ANIMAL_YIELDS = {
    // Small prey - low yields
    [NpcType.RABBIT]: { meat: 10, leather: 5 },
    [NpcType.SQUIRREL]: { meat: 5, leather: 3 },
    [NpcType.MOUSE]: { meat: 2, leather: 1 },
    [NpcType.RAT]: { meat: 3, leather: 2 },
    // Medium prey - moderate yields
    [NpcType.DEER]: { meat: 40, leather: 25 },
    [NpcType.BOAR]: { meat: 35, leather: 20 },
    [NpcType.GOAT]: { meat: 25, leather: 15 },
    [NpcType.SHEEP]: { meat: 20, leather: 12 },
    // Large herbivores - high yields
    [NpcType.HORSE]: { meat: 60, leather: 40 },
    [NpcType.COW]: { meat: 70, leather: 45 },
    [NpcType.MOOSE]: { meat: 55, leather: 35 },
    [NpcType.BISON]: { meat: 80, leather: 50 },
    // Small predators - lower quality leather
    [NpcType.FOX]: { meat: 12, leather: 10 },
    [NpcType.WILDCAT]: { meat: 10, leather: 8 },
    [NpcType.BADGER]: { meat: 15, leather: 10 },
    [NpcType.WEASEL]: { meat: 5, leather: 4 },
    // Medium predators - decent yields
    [NpcType.WOLF]: { meat: 25, leather: 20 },
    [NpcType.COYOTE]: { meat: 18, leather: 14 },
    [NpcType.LYNX]: { meat: 20, leather: 18 },
    [NpcType.HYENA]: { meat: 22, leather: 16 },
    // Large predators - high yields, valuable fur/leather
    [NpcType.BEAR]: { meat: 65, leather: 55 },
    [NpcType.LION]: { meat: 50, leather: 40 },
    [NpcType.TIGER]: { meat: 55, leather: 45 },
    [NpcType.COUGAR]: { meat: 40, leather: 35 },
    // Birds - prey (mostly meat, minimal leather)
    [NpcType.CHICKEN]: { meat: 8, leather: 0 },
    [NpcType.TURKEY]: { meat: 15, leather: 0 },
    [NpcType.PHEASANT]: { meat: 10, leather: 0 },
    [NpcType.DUCK]: { meat: 8, leather: 0 },
    [NpcType.GOOSE]: { meat: 12, leather: 0 },
    // Birds - predator (minimal yields)
    [NpcType.HAWK]: { meat: 5, leather: 0 },
    [NpcType.EAGLE]: { meat: 8, leather: 0 },
    [NpcType.OWL]: { meat: 4, leather: 0 },
    [NpcType.RAVEN]: { meat: 3, leather: 0 },
    // Reptiles
    [NpcType.SNAKE]: { meat: 5, leather: 4 },
    [NpcType.LIZARD]: { meat: 3, leather: 2 },
    [NpcType.TURTLE]: { meat: 8, leather: 6 },
    [NpcType.CROCODILE]: { meat: 30, leather: 35 },
    // Aquatic
    [NpcType.FISH]: { meat: 5, leather: 0 },
    [NpcType.SALMON]: { meat: 8, leather: 0 },
    [NpcType.CRAB]: { meat: 4, leather: 0 },
    [NpcType.FROG]: { meat: 2, leather: 0 },
};
export const getNpcYield = ({ npcType }) => {
    return ANIMAL_YIELDS[npcType] ?? { meat: 5, leather: 2 };
};
// ============================================================================
// Rot Quality Multipliers
// ============================================================================
export const ROT_QUALITY_MULTIPLIERS = {
    [RotStage.FRESH]: 1.0, // Full yield
    [RotStage.STALE]: 0.6, // 60% yield
    [RotStage.ROTTED]: 0.0, // No usable meat
    [RotStage.SKELETON]: 0.0, // Nothing left
};
export const getYieldMultiplier = ({ rotStage }) => {
    return ROT_QUALITY_MULTIPLIERS[rotStage] ?? 0;
};
export const createDeathStory = ({ killerName = null, killerType = null, killerNpcType = null, weaponUsed = null, finalBlowDescription = null, combatDurationTicks = null, wasDefending = false, location, deathTick, }) => ({
    killerName,
    killerType,
    killerNpcType,
    weaponUsed,
    finalBlowDescription,
    combatDurationTicks,
    wasDefending,
    location,
    deathTick,
});
// ============================================================================
// Decay Constants
// ============================================================================
export const DECAY_CONSTANTS = {
    BASE_DECAY_PER_TICK: 0.00005, // ~5.5 real hours to skeleton at 20 TPS
    FROZEN_MULTIPLIER: 0.01, // 1% decay rate when frozen
    OUTDOOR_MULTIPLIER: 1.5, // 50% faster outdoors
    HOT_MULTIPLIER: 2.0, // 2x decay when hot (>30C)
};
//# sourceMappingURL=corpse.js.map